import { ValidityDefinition } from "../models/site.model";

// Tree model for validity definitions. Used for convenience, in the database the validity
// tree is stored as an array of validity paths.
export type ValidityTreeType = { [key: string]: ValidityTreeType };

/***
 * Add default values to a validity string.
 * The definition is a tree structure. The default value is used if the validity
 * string does not contain a value at a certain level.
 */
export function addDefaultValidity(validity: string, definition: ValidityDefinition | null): string {
    const validityArray = validity.split(":");
    const thisLevel = validityArray[0];
    const thisLevelResolved: string = thisLevel || definition?.default || "";
    const pos: number = definition?.values?.findIndex?.((item) => item.value === thisLevelResolved) ?? -1;
    let remainingResolved: string = "";
    if (validityArray.length > 1 || pos !== -1) {
        remainingResolved = addDefaultValidity(validityArray.slice(1).join(":"), definition?.values?.[pos]?.definition || null);
    }
    return `${thisLevelResolved}${(thisLevelResolved && remainingResolved) ? ":" : ""}${remainingResolved}`;
}

/***
 * Get the label for a path in a validity tree.
 * The label is the name that is defined in the validity definition that
 * corresponds to the path.
 */
export function getValidityPathLabel(path: Array<string>, definition: ValidityDefinition | null): string {
    let label = "";
    for (let value of path) {
        label = definition?.name || "";
        const pos: number = definition?.values?.findIndex((item) => item.value === value) ?? -1;
        definition = definition?.values?.[pos]?.definition || null;
    }
    return label;
}

/***
 * Flatten a validity tree to an array of strings.
 * The tree is a nested object where the keys are the values in the validity path.
 * The result is an array of strings where each string is a path in the tree.
 */
export const flattenValidityTree = (tree: ValidityTreeType): string[] => {
    return flattenValidityTreeRecursively(tree, "");
}

const flattenValidityTreeRecursively = (tree: ValidityTreeType, prefix: string): string[] => {
    const result: string[] = [];
    for (let key in tree) {
        const fullKey = prefix ? (prefix + ":" + key) : key;
        const value = tree[key];
        result.push(fullKey);
        if (typeof value === "object") {
            result.push(...flattenValidityTreeRecursively(value, fullKey));
        }
    }
    return result;
}

/***
 * Unflatten an array of strings to a validity tree.
 * The tree is a nested object where the keys are the values in the validity path.
 * The input is an array of strings where each string is a path in the tree.
 */
export const unflattenValidityTree = (paths: string[]): ValidityTreeType => {
    const tree: ValidityTreeType = {};
    for (let path of paths) {
        const parts = path.split(":");
        let current = tree;
        for (let part of parts) {
            if (!current[part]) {
                current[part] = {};
            }
            current = current[part];
        }
    }
    return tree;
}
